import React, { useState, useEffect, useCallback } from 'react';
import { cmsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, ArrowsClockwise, FileText, Users, Calendar, Quotes,
  CheckCircle, XCircle, ArrowsDownUp, X,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';

// ---- Content-type schema (source of truth for admin form + public renderers) ----
const TYPES = [
  {
    key: 'case_study',
    label: 'Case studies',
    singular: 'case study',
    tabLabel: 'Case Studies',
    icon: Quotes,
    tint: '#002677',
    page: '/use-cases',
    fields: [
      { key: 'quote',      label: 'Quote',        type: 'textarea', required: true },
      { key: 'author',     label: 'Author',       type: 'text',     required: true },
      { key: 'role',       label: 'Role',         type: 'text' },
      { key: 'company',    label: 'Company',      type: 'text' },
      { key: 'stat',       label: 'Stat',         type: 'text',     hint: 'e.g. “+2.4× IRR”' },
      { key: 'stat_label', label: 'Stat caption', type: 'text',     hint: 'e.g. “Post-transformation”' },
    ],
  },
  {
    key: 'partner',
    label: 'Partners',
    singular: 'partner',
    tabLabel: 'Partners',
    icon: Users,
    tint: '#00B140',
    page: '/partners',
    fields: [
      { key: 'name',   label: 'Partner name', type: 'text', required: true },
      { key: 'region', label: 'Region / specialisation', type: 'text', required: true, hint: 'e.g. “EU · Nordic M&A”' },
      { key: 'tier',   label: 'Tier',         type: 'select', options: ['Platinum', 'Gold', 'Silver'], required: true },
    ],
  },
  {
    key: 'event',
    label: 'Events',
    singular: 'event',
    tabLabel: 'Events',
    icon: Calendar,
    tint: '#E4002B',
    page: '/resources',
    fields: [
      { key: 'date',     label: 'Date', type: 'text',  required: true, hint: 'Format: "Mar 12" — month abbr + day.' },
      { key: 'title',    label: 'Title', type: 'text', required: true },
      { key: 'location', label: 'Location / format', type: 'text', required: true },
      { key: 'cta',      label: 'CTA label', type: 'text', hint: 'default: "Register"' },
    ],
  },
  {
    key: 'resource',
    label: 'Featured resources',
    singular: 'resource',
    tabLabel: 'Resources',
    icon: FileText,
    tint: '#8347AD',
    page: '/resources',
    fields: [
      { key: 'kind',       label: 'Kind',          type: 'text',     required: true, hint: 'e.g. Whitepaper, Webinar, Story' },
      { key: 'kind_tint',  label: 'Kind colour',   type: 'text',     hint: 'Hex, e.g. #8347AD' },
      { key: 'title',      label: 'Title',         type: 'text',     required: true },
      { key: 'desc',       label: 'Description',   type: 'textarea', required: true },
      { key: 'cta',        label: 'CTA label',     type: 'text',     hint: 'default: "Read more"' },
    ],
  },
];

const TYPE_BY_KEY = Object.fromEntries(TYPES.map((t) => [t.key, t]));

export default function CMSAdminPage() {
  const { user } = useAuth();
  const [activeType, setActiveType] = useState('case_study');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | { ...item }

  const isAdmin = user && user.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await cmsApi.list(activeType);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const handleSave = async (payload, itemId) => {
    try {
      if (itemId) {
        await cmsApi.update(itemId, payload);
        toast.success('Item updated');
      } else {
        await cmsApi.create({ type: activeType, ...payload });
        toast.success('Item created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return; // eslint-disable-line no-alert
    try {
      await cmsApi.delete(id);
      toast.success('Item deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await cmsApi.update(item.id, { active: !item.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-10" data-testid="cms-admin-only">
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <p className="font-heading text-lg font-semibold">Admin access required</p>
          <p className="text-sm text-muted-foreground mt-1">You must be an admin to edit CMS content.</p>
        </div>
      </div>
    );
  }

  const typeMeta = TYPE_BY_KEY[activeType];

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="cms-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Admin · CMS</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Marketing Content</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit case studies, partners, events, and featured resources. Changes go live instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="cms-refresh">
            <ArrowsClockwise size={16} className="mr-2" /> Refresh
          </Button>
          <Button
            onClick={() => setEditing('new')}
            className="rounded-sm"
            style={{ background: typeMeta.tint }}
            data-testid="cms-new-btn"
          >
            <Plus size={16} className="mr-2" weight="bold" /> New {typeMeta.singular}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-6 overflow-x-auto" data-testid="cms-tabs">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = activeType === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setActiveType(t.key); setEditing(null); }}
                className={`relative flex items-center gap-2 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`cms-tab-${t.key}`}
              >
                <Icon size={16} weight={active ? 'fill' : 'duotone'} color={active ? t.tint : undefined} />
                {t.tabLabel}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px]" style={{ background: t.tint }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: list */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState type={typeMeta} onNew={() => setEditing('new')} />
          ) : (
            <ul className="space-y-3" data-testid="cms-items">
              {items.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  typeMeta={typeMeta}
                  onEdit={() => setEditing(it)}
                  onDelete={() => handleDelete(it.id)}
                  onToggle={() => handleToggleActive(it)}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Right: editor */}
        <div className="lg:col-span-2">
          {editing ? (
            <Editor
              key={editing === 'new' ? 'new' : editing.id}
              typeMeta={typeMeta}
              initial={editing === 'new' ? null : editing}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <SidebarHint typeMeta={typeMeta} />
          )}
        </div>
      </div>
    </div>
  );
}

// =======================================================================
function ItemRow({ item, typeMeta, onEdit, onDelete, onToggle }) {
  const primary = item.data?.title || item.data?.name || item.data?.quote || '(Untitled)';
  const secondary = item.data?.region || item.data?.author || item.data?.kind || item.data?.location || '';
  return (
    <li
      className={`bg-card border rounded-sm p-4 flex items-start gap-4 hover:shadow-md transition-all ${
        item.active ? 'border-border' : 'border-dashed border-border/60 opacity-70'
      }`}
      data-testid={`cms-row-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
            #{item.order ?? 0}
          </span>
          {item.active ? (
            <Badge className="bg-[#00B140]/15 text-[#00813A] hover:bg-[#00B140]/15">Active</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">Hidden</Badge>
          )}
          <span className="text-[11px] text-muted-foreground">
            Updated {formatDateTime(item.updated_at || item.created_at)}
          </span>
        </div>
        <p className="mt-2 font-medium text-sm line-clamp-2">{primary}</p>
        {secondary && <p className="text-xs text-muted-foreground mt-0.5 truncate">{secondary}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground"
          data-testid={`cms-toggle-${item.id}`}
          title={item.active ? 'Hide' : 'Show'}
        >
          {item.active ? <CheckCircle size={16} weight="fill" className="text-[#00B140]" /> : <XCircle size={16} weight="duotone" />}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`cms-edit-${item.id}`}>
          <PencilSimple size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:bg-destructive/10"
          data-testid={`cms-delete-${item.id}`}
        >
          <Trash size={16} />
        </Button>
      </div>
    </li>
  );
}

// =======================================================================
function Editor({ typeMeta, initial, onSave, onCancel }) {
  const [data, setData] = useState(() => initial?.data || {});
  const [order, setOrder] = useState(initial?.order ?? 0);
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Required-field check
    for (const f of typeMeta.fields) {
      if (f.required && !(`${data[f.key] ?? ''}`.trim())) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    await onSave({ data, order: Number(order) || 0, active }, initial?.id);
    setSaving(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-card border border-border rounded-sm p-5 sticky top-6 space-y-4"
      data-testid="cms-editor"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            {initial ? 'Edit' : 'New'} · {typeMeta.label}
          </p>
          <h3 className="font-heading text-lg font-semibold tracking-tight">
            {initial ? 'Update item' : 'Create item'}
          </h3>
        </div>
        <Button variant="ghost" size="sm" type="button" onClick={onCancel} data-testid="cms-editor-close">
          <X size={16} weight="bold" />
        </Button>
      </div>

      {typeMeta.fields.map((f) => (
        <FieldRow key={f.key} field={f} value={data[f.key] ?? ''} onChange={(v) => set(f.key, v)} />
      ))}

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-1">
            <ArrowsDownUp size={12} /> Order
          </Label>
          <Input
            type="number"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            className="mt-1.5 rounded-sm"
            data-testid="cms-order"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={active} onCheckedChange={setActive} data-testid="cms-active" />
            <span className="text-sm font-medium">{active ? 'Active' : 'Hidden'}</span>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button
          type="submit"
          disabled={saving}
          className="flex-1 rounded-sm"
          style={{ background: typeMeta.tint }}
          data-testid="cms-save"
        >
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create item'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          Cancel
        </Button>
      </div>
    </form>
  );
}

function FieldRow({ field, value, onChange }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">
        {field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">
        {field.type === 'textarea' && (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="rounded-sm"
            data-testid={`cms-field-${field.key}`}
          />
        )}
        {field.type === 'text' && (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-sm"
            data-testid={`cms-field-${field.key}`}
          />
        )}
        {field.type === 'select' && (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="rounded-sm" data-testid={`cms-field-${field.key}`}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {field.hint && (
        <p className="mt-1 text-[11px] text-muted-foreground">{field.hint}</p>
      )}
    </div>
  );
}

// =======================================================================
function EmptyState({ type, onNew }) {
  const Icon = type.icon;
  return (
    <div className="bg-card border border-dashed border-border rounded-sm p-10 text-center">
      <div
        className="h-12 w-12 rounded-sm mx-auto flex items-center justify-center mb-4"
        style={{ background: `${type.tint}1A` }}
      >
        <Icon size={24} weight="duotone" color={type.tint} />
      </div>
      <p className="font-heading text-lg font-semibold">No {type.label.toLowerCase()} yet</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
        Add your first {type.singular} — it&rsquo;ll appear on <code className="bg-muted/40 px-1 rounded">{type.page}</code> immediately.
      </p>
      <Button onClick={onNew} className="mt-5 rounded-sm" style={{ background: type.tint }}>
        <Plus size={14} className="mr-2" weight="bold" /> Add first item
      </Button>
    </div>
  );
}

function SidebarHint({ typeMeta }) {
  const Icon = typeMeta.icon;
  return (
    <div className="bg-muted/20 border border-dashed border-border rounded-sm p-6 text-center">
      <div
        className="h-10 w-10 rounded-sm mx-auto flex items-center justify-center mb-3"
        style={{ background: `${typeMeta.tint}1A` }}
      >
        <Icon size={20} weight="duotone" color={typeMeta.tint} />
      </div>
      <p className="text-sm font-medium">Select an item to edit</p>
      <p className="text-xs text-muted-foreground mt-1">
        Or click <strong>New</strong> in the toolbar to add a new {typeMeta.singular}.
      </p>
    </div>
  );
}
